import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  Switch,
  Table,
  Space,
  Tag,
  Typography,
  Input,
  Tooltip,
  Checkbox,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDeliveryAssignments,  
  saveDeliveryAssignment,
  deleteDeliveryAssignment,
  fetchDeliveryRoutes,
} from "../deliveryThunks";

const dayOptions = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
];

const { Text } = Typography;

const DeliveryAssignments = ({ deliveryUsers = []}) => {
  const dispatch = useDispatch();
  const { routes, routeAssignments } = useSelector((state) => state.delivery);

  const [open, setOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({current: 1, pageSize: 10});
  const [form] = Form.useForm();

  const selectedEmployeeId = Form.useWatch("employee_id", form);
  // const selectedScheduleType = Form.useWatch('schedule_type', form);

  const employees = useMemo(
    () =>
      deliveryUsers.map((u) => ({
        employee_id: u.employee_id,
        user_id: u.id,
        employee_name: u.full_name,
      })),
    [deliveryUsers]
  );

  useEffect(() => {
    dispatch(fetchDeliveryAssignments());
    dispatch(fetchDeliveryRoutes());
  }, [dispatch]);

  const employeeOptions = useMemo(() => {
    return employees.map((emp) => ({
      label: emp.employee_name || emp.name || emp.user_name,
      value: emp.employee_id,
    }));
  }, [employees]);

  const replacementEmployeeOptions = useMemo(() => {
    return employees
      .filter((emp) => emp.employee_id !== selectedEmployeeId)
      .map((emp) => ({
        label: emp.employee_name || emp.name || emp.user_name,
        value: emp.employee_id,
      }));
  }, [employees, selectedEmployeeId]);

  useEffect(() => {
    const replacementEmployee = form.getFieldValue("replacement_employee_id");
    if (replacementEmployee === selectedEmployeeId) {
      form.setFieldValue("replacement_employee_id", undefined);
    }
  }, [selectedEmployeeId, form]);

  // useEffect(() => {
  //   if (!open || !selectedAssignment) return;

  //   form.setFieldsValue({
  //     employee_id: selectedAssignment.employee_id,
  //     route_id: selectedAssignment.route_id,
  //     assignment_date: selectedAssignment.assignment_date
  //       ? dayjs(selectedAssignment.assignment_date)
  //       : dayjs(),
  //     // schedule_end_date: selectedAssignment.schedule_end_date
  //     //   ? dayjs(selectedAssignment.schedule_end_date)
  //     //   : null,
  //     // schedule_days:
  //     //   selectedAssignment.schedule_days || [],
  //     // schedule_type:
  //     //   selectedAssignment.schedule_type || "once",
  //     replacement_employee_id:
  //       selectedAssignment.replacement_employee_id,
  //     is_temporary:
  //       !!selectedAssignment.is_temporary,
  //     status: selectedAssignment.status || "assigned",
  //   });
  // }, [open, selectedAssignment, form]);

  useEffect(() => {
    if (!open) return;

    if (selectedAssignment) {
      form.setFieldsValue({
        employee_id: selectedAssignment.employee_id,
        route_id: selectedAssignment.route_id,
        assignment_date: selectedAssignment.assignment_date
          ? dayjs(selectedAssignment.assignment_date)
          : dayjs(),
        replacement_employee_id:
          selectedAssignment.replacement_employee_id,
        is_temporary: !!selectedAssignment.is_temporary,
        status: selectedAssignment.status || "assigned",
      });
    } else {
      form.setFieldsValue({
        assignment_date: dayjs(),
        is_temporary: false,
        status: "assigned",
      });
    }
  }, [open, selectedAssignment, form]);

  const openAddModal = () => {
    setSelectedAssignment(null);
    form.resetFields();
    setOpen(true);
  };

  // const openEditModal = (record) => {
  // setSelectedAssignment(record);
  // setOpen(true);

  //   setTimeout(() => {
  //     form.setFieldsValue({
  //       employee_id: record.employee_id,
  //       route_id: record.route_id,
  //       assignment_date: record.assignment_date
  //         ? dayjs(record.assignment_date)
  //         : null,
  //       schedule_end_date: record.schedule_end_date
  //         ? dayjs(record.schedule_end_date)
  //         : null,
  //       schedule_days: record.schedule_days || [],
  //       schedule_type: record.schedule_type || "once",
  //       replacement_employee_id:
  //         record.replacement_employee_id,
  //       is_temporary: !!record.is_temporary,
  //       status: record.status,
  //     });
  //   }, 0);
  // };

  const openEditModal = (record) => {
    setSelectedAssignment(record);
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();    
      
      // Sanitize: Clear schedule_days if the schedule_type doesn't use them
      // const cleanedValues = {
      //   ...values,
      //   schedule_days: ["weekly", "custom"].includes(values.schedule_type) 
      //     ? values.schedule_days 
      //     : []
      // };

      await dispatch(
        saveDeliveryAssignment({
          id: selectedAssignment?.id,
          // ...cleanedValues,
          assignment_date: values.assignment_date
          ? values.assignment_date.format("YYYY-MM-DD")
          : null,
          // schedule_end_date: values.schedule_end_date ? dayjs(values.schedule_end_date).format("YYYY-MM-DD") : null

          employee_id: values.employee_id,
          route_id: values.route_id,
          replacement_employee_id: values.replacement_employee_id || null,
          is_temporary: values.is_temporary ? 1 : 0,
          status: values.status || "assigned",
        }),
      ).unwrap();

      message.success(
        selectedAssignment
          ? "Delivery route assignment updated successfully."
          : "Delivery route assignment created successfully."
      );

      setOpen(false);
      form.resetFields();
      setSelectedAssignment(null);
    } catch (error) {
    console.log("ASSIGNMENT ERROR:", error);

    if (error?.errorFields) {
      console.log("FORM VALIDATION ERROR:", error.errorFields);
      return;
    }

    message.error(
      error?.message ||
      error?.response?.data?.message ||
      "Failed to save delivery route assignment."
    );
  }
  };

  const filteredAssignments = routeAssignments.filter((item) => {
    const employeeName = item.employee_name?.toLowerCase() || "";
    const routeName = item.route_name?.toLowerCase() || "";
    return employeeName.includes(search.toLowerCase()) || routeName.includes(search.toLowerCase());
  });

  const statusColorMap = {
    assigned: "blue",
    in_progress: "orange",
    completed: "green",
    cancelled: "red",
  };

  const columns = [
    {
      title: "#",
      key: "serialNumber",
      width: 65,
      align: "center",
      render: (_, __, index) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: "Employee",
      render: (_, record) => <Text>{record.employee_name || record.name}</Text>,
    },
    {
      title: "Route",
      dataIndex: "route_name",
    },
    // {
    //   title: "Schedule Type",
    //   dataIndex: "schedule_type",
    //   render: (_, record) => {
    //     const label =
    //       record.schedule_type?.charAt(0).toUpperCase() +
    //       record.schedule_type?.slice(1);

    //     const showScheduleDays = ["weekly", "custom"].includes(
    //       record.schedule_type,
    //     );

    //     const days =
    //       showScheduleDays && record.schedule_days?.length > 0
    //         ? record.schedule_days
    //             .map(
    //               (d) => d.charAt(0).toUpperCase() + d.slice(1),
    //             )
    //             .join(", ")
    //         : null;

    //     const tag = (
    //       <Tag color="blue">
    //         {label}

    //         {showScheduleDays && days && (
    //           <InfoCircleOutlined
    //             style={{
    //               marginLeft: 4,
    //               color: "#0e3e8b",
    //               cursor: "pointer",
    //             }}
    //           />
    //         )}
    //       </Tag>
    //     );

    //     if (!showScheduleDays || !days) {
    //       return tag;
    //     }

    //     return (
    //       <Tooltip
    //         placement="bottomLeft"
    //         color="#ffffff"
    //         trigger={["hover", "click"]}
    //         title={
    //           <div style={{ color: "#000" }}>
    //             <div
    //               style={{
    //                 fontWeight: 600,
    //                 marginBottom: 4,
    //               }}
    //             >
    //               Assigned Days
    //             </div>
    //             <div>{days}</div>
    //           </div>
    //         }
    //       >
    //         {tag}
    //       </Tooltip>
    //     );
    //   },
    // },
    // {
    //   title: "Schedule",
    //   render: (_, record) => {
    //     if (record.schedule_type === "once") {
    //       return dayjs(record.assignment_date).format("DD-MM-YYYY");
    //     }
    //     return `${dayjs(record.assignment_date).format("DD-MM-YYYY")} → ${dayjs(record.schedule_end_date).format("DD-MM-YYYY")}`;
    //   },
    // },
    {
      title: "Replacement",
      render: (_, record) => {
        if (!record.replacement_employee_name) return "-";
        return <Text>{record.replacement_employee_name}</Text>;
      },
    },
    {
      title: "Temporary",
      dataIndex: "is_temporary",
      render: (v) => v ? <Tag color="orange">Temporary</Tag> : <Tag color="green">Regular</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <Tag color={statusColorMap[v]}>{v.charAt(0).toUpperCase() + v.slice(1)}</Tag>,
    },
    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined/>} onClick={() => openEditModal(record)}/>
          <Button
            danger
            icon={<DeleteOutlined/>}
            onClick={() => dispatch(deleteDeliveryAssignment({ id: record.id }))}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="Delivery Assignment">
        <Row
          gutter={[16, 16]}
          style={{ marginBottom: 16 }}
          justify="space-between"
          align="middle"
        >
          <Col xs={24} sm={14} md={10} lg={8}>
            <Input
              placeholder="Search employee or route"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={10} md={6} style={{ textAlign: "right" }}>
            <Button type="primary" onClick={openAddModal} block>
              Assign Delivery Route
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredAssignments}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => {
              setPagination({
                current: page,
                pageSize,
              });
            },
            onShowSizeChange: (current, size) => {
              setPagination({
                current: 1,
                pageSize: size,
              });
            },
          }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      <Modal
        open={open}
        title={selectedAssignment ? "Edit Assignment" : "Assign Route"}
        onCancel={() => setOpen(false)}
        destroyOnClose
        onOk={handleSave}
        width={700}
        style={{ top: 20 }}
      >
        <Form layout="vertical" form={form} key={selectedAssignment?.id || "new"}>
          <Row gutter={[16, 0]}>            
            <Col xs={24} md={12}>
              <Form.Item
                label="Employee"
                name="employee_id"
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={employeeOptions}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Route"
                name="route_id"
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={routes.map((route) => ({
                    label: route.name,
                    value: route.id,
                  }))}
                />
              </Form.Item>
            </Col>

            {/* <Col xs={24} sm={12} md={12}>
              <Form.Item
                label="Start Date"
                name="assignment_date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col> */}

            {/* <Col xs={24} sm={12} md={12}>
              <Form.Item
                label={
                  <Space>
                    Schedule Type
                    <Tooltip title="Configure recurring route execution pattern for employee operational workflow.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="schedule_type"
                initialValue="once"
              >
                <Select
                  onChange={(value) => {
                    if (value === "once") {
                      form.setFieldValue("schedule_end_date", null);
                    }
                  }}
                  options={[
                    { label: "One Time", value: "once" },
                    { label: "Daily", value: "daily" },
                    { label: "Weekly", value: "weekly" },
                    // { label: "Monthly", value: "monthly" },
                    { label: "Custom", value: "custom" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col 
              span={24} 
              style={{ 
                display: ["weekly", "custom"].includes(selectedScheduleType) ? "block" : "none" 
              }}
            >
              <Form.Item 
                label="Schedule Days" 
                name="schedule_days"
              >
                <Checkbox.Group options={dayOptions} />
              </Form.Item>
            </Col> */}

            {/* <Col xs={24} sm={12} md={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.schedule_type !== currentValues.schedule_type
                }
              >
                {({ getFieldValue }) => {
                  const isOnce = getFieldValue("schedule_type") === "once";
                  return (
                    <Form.Item
                      label="Schedule End Date"
                      name="schedule_end_date"
                      rules={[
                        {
                          required: !isOnce,
                          message: "Please select a schedule end date",
                        },
                      ]}
                    >
                      <DatePicker style={{ width: "100%" }} disabled={isOnce} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col> */}

            <Col xs={24} sm={12} md={12}>
              <Form.Item
                label="Assignment Date"
                name="assignment_date"
                rules={[
                  {
                    required: true,
                    message: "Please select assignment date",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12}>
              <Form.Item
                label={
                  <Space>
                    Replacement Employee
                    <Tooltip
                      title="Backup employee who can execute this route if the primary employee is unavailable."
                      placement="bottom"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="replacement_employee_id"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={replacementEmployeeOptions}
                />
              </Form.Item>
            </Col>

            <Col xs={12} md={12}>
              <Form.Item
                label={
                  <Space>
                    Temporary Assignment
                    <Tooltip
                      title="Used for temporary route coverage without affecting permanent route planning."
                      placement="bottom"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="is_temporary"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>

            <Col xs={12} md={12}>
              <Form.Item
                label={
                  <Space>
                    Status
                    <Tooltip
                      title="Operational state of this route assignment."
                      placement="bottom"
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="status"
              >
                <Select
                  options={[
                    { label: "Assigned", value: "assigned" },
                    { label: "In Progress", value: "in_progress" },
                    { label: "Completed", value: "completed" },
                    { label: "Cancelled", value: "cancelled" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default DeliveryAssignments;